import React, { useState } from 'react';
import { Plus, Save } from 'lucide-react';

export default function ContentEditorPanel({ fatouConfig, onCreate, onRefresh }) {
  const [editMode, setEditMode] = useState(false);
  const [refinementData, setRefinementData] = useState({
    type: 'faq_add',
    category: 'general',
    priority: 'medium',
    title: '',
    currentContent: null,
    proposedContent: {},
    rationale: '',
    impactAssessment: ''
  });

  const [faqForm, setFaqForm] = useState({
    question: '',
    answer: '',
    category: 'general',
    priority: 'medium'
  });

  const handleCreateFAQRefinement = () => {
    if (!faqForm?.question || !faqForm?.answer) {
      alert('Veuillez remplir tous les champs requis');
      return;
    }

    const refinement = {
      type: 'faq_add',
      category: faqForm?.category,
      priority: faqForm?.priority,
      title: `Ajout FAQ: ${faqForm?.question?.substring(0, 50)}...`,
      currentContent: null,
      proposedContent: {
        id: Date.now(),
        question: faqForm?.question,
        answer: faqForm?.answer,
        category: faqForm?.category,
        priority: faqForm?.priority
      },
      rationale: 'Nouvelle FAQ suggérée par l\'administrateur',
      impactAssessment: 'Amélioration de la base de connaissances Fatou',
      source: 'admin'
    };

    onCreate(refinement);
    setFaqForm({
      question: '',
      answer: '',
      category: 'general',
      priority: 'medium'
    });
    setEditMode(false);
  };

  const categories = [
    { value: 'general', label: 'Général' },
    { value: 'pricing', label: 'Tarifs' },
    { value: 'shipping', label: 'Expédition' },
    { value: 'tracking', label: 'Suivi' },
    { value: 'documents', label: 'Documents' },
    { value: 'products', label: 'Produits' },
    { value: 'delivery', label: 'Livraison' }
  ];

  return (
    <div className="space-y-6">
      {/* Current FAQs Overview */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">FAQs Actuelles</h3>
          <button
            onClick={onRefresh}
            className="text-sm text-green-600 hover:text-green-700"
          >
            Actualiser
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-2xl font-bold text-blue-600">{fatouConfig?.faqs?.length || 0}</p>
            <p className="text-sm text-gray-600">FAQs Totales</p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <p className="text-2xl font-bold text-green-600">{fatouConfig?.products?.length || 0}</p>
            <p className="text-sm text-gray-600">Produits</p>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <p className="text-2xl font-bold text-purple-600">{fatouConfig?.coherenceRules?.length || 0}</p>
            <p className="text-sm text-gray-600">Règles de Cohérence</p>
          </div>
        </div>
      </div>
      {/* Add New FAQ */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Créer une Nouvelle FAQ</h3>
          {!editMode && (
            <button
              onClick={() => setEditMode(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <Plus size={18} />
              <span>Nouvelle FAQ</span>
            </button>
          )}
        </div>

        {editMode && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Question *</label>
              <input
                type="text"
                value={faqForm?.question}
                onChange={(e) => setFaqForm({ ...faqForm, question: e?.target?.value })}
                placeholder="Quelle est votre question?"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Réponse *</label>
              <textarea
                value={faqForm?.answer}
                onChange={(e) => setFaqForm({ ...faqForm, answer: e?.target?.value })}
                placeholder="Réponse détaillée..."
                rows={6}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Catégorie</label>
                <select
                  value={faqForm?.category}
                  onChange={(e) => setFaqForm({ ...faqForm, category: e?.target?.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  {categories?.map(cat => (
                    <option key={cat?.value} value={cat?.value}>{cat?.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Priorité</label>
                <select
                  value={faqForm?.priority}
                  onChange={(e) => setFaqForm({ ...faqForm, priority: e?.target?.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  <option value="low">Basse</option>
                  <option value="medium">Moyenne</option>
                  <option value="high">Haute</option>
                </select>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={handleCreateFAQRefinement}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <Save size={18} />
                <span>Créer la Suggestion</span>
              </button>
              <button
                onClick={() => {
                  setEditMode(false);
                  setFaqForm({
                    question: '',
                    answer: '',
                    category: 'general',
                    priority: 'medium'
                  });
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Annuler
              </button>
            </div>
          </div>
        )}

        {!editMode && (
          <p className="text-gray-600 text-sm">
            Cliquez sur "Nouvelle FAQ" pour créer une suggestion d'amélioration de la base de connaissances.
          </p>
        )}
      </div>
      {/* Existing FAQs List */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">FAQs Existantes</h3>
        <div className="space-y-3 max-h-[400px] overflow-y-auto">
          {fatouConfig?.faqs?.length === 0 ? (
            <p className="text-gray-600 text-sm text-center py-4">Aucune FAQ configurée</p>
          ) : (
            fatouConfig?.faqs?.map((faq, index) => (
              <div key={faq?.id || index} className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 mb-1">{faq?.question}</p>
                    <p className="text-sm text-gray-600 mb-2">{faq?.answer}</p>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">{faq?.category}</span>
                      <span className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded">{faq?.priority}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}